'use client'

import * as React from 'react'
import { LuCheck, LuChevronDown } from 'react-icons/lu'
import { TbWaveSine } from 'react-icons/tb'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import type { ModalityType, ModalitySelection } from '@/data/types/analytics.types'

interface ModalityOption {
  value: ModalityType
  label: string
}

const modalityOptions: ModalityOption[] = [
  { value: 'text', label: 'Text' },
  { value: 'image', label: 'Image' },
  { value: 'audio', label: 'Audio' },
  { value: 'video', label: 'Video' },
]

interface ModalitySelectorProps {
  value?: ModalitySelection
  onChange?: (selection: ModalitySelection) => void
  className?: string
}

// Default selections
export const ALL_MODALITIES_SELECTION: ModalitySelection = { all: true, modalities: [] }
export const NO_MODALITIES_SELECTION: ModalitySelection = { all: false, modalities: [] }

const ModalitySelector = ({
  value: externalValue,
  onChange: externalOnChange,
  className,
}: ModalitySelectorProps) => {
  const [open, setOpen] = React.useState(false)

  // Internal state for uncontrolled mode
  const [internalValue, setInternalValue] = React.useState<ModalitySelection>(ALL_MODALITIES_SELECTION)

  // Use external value if provided, otherwise use internal state
  const value = externalValue !== undefined ? externalValue : internalValue
  const onChange = externalOnChange || setInternalValue

  // Check selection states
  const allModalities = modalityOptions.map(opt => opt.value)
  const isAllSelected = value.all || (value.modalities.length === allModalities.length)
  const isNoneSelected = !value.all && value.modalities.length === 0
  const isSomeSelected = !isNoneSelected && !isAllSelected
  const selectedCount = value.all ? allModalities.length : value.modalities.length

  // Get display text for the button
  const getDisplayText = () => {
    if (isAllSelected) return 'All Modalities'
    if (isNoneSelected) return 'No Modality'
    if (value.modalities.length === 1) {
      const option = modalityOptions.find(opt => opt.value === value.modalities[0])
      return option?.label || 'Modality'
    }
    return `${selectedCount} Modalities`
  }

  // Toggle Select All
  const toggleSelectAll = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (isAllSelected) {
      onChange(NO_MODALITIES_SELECTION)
    } else {
      onChange(ALL_MODALITIES_SELECTION)
    }
  }

  // Toggle individual modality selection
  const toggleModality = (modality: ModalityType, e?: React.MouseEvent) => {
    e?.stopPropagation()

    if (value.all) {
      // If all is selected, switching to all except this one
      const newModalities = allModalities.filter(m => m !== modality)
      onChange({ all: false, modalities: newModalities })
      return
    }

    const isSelected = value.modalities.includes(modality)
    let newModalities: ModalityType[]

    if (isSelected) {
      // Remove this modality
      newModalities = value.modalities.filter(m => m !== modality)
    } else {
      // Add this modality
      newModalities = [...value.modalities, modality]
    }

    // Check if all modalities are now selected
    if (newModalities.length === allModalities.length) {
      onChange(ALL_MODALITIES_SELECTION)
    } else {
      onChange({ all: false, modalities: newModalities })
    }
  }

  // Clear all selections
  const clearAll = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    onChange(NO_MODALITIES_SELECTION)
  }

  // Check if a modality is selected
  const isModalitySelected = (modality: ModalityType) => {
    return value.all || value.modalities.includes(modality)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="transparent-outline"
          size="xs"
          className={cn(
            'hover:bg-[#F2EEFF] hover:text-[#654EDA] hover:border-[#E2DAFF]',
            'focus-visible:ring-[#654EDA]/20 focus-visible:border-[#654EDA] focus-visible:ring-2',
            open && 'bg-[#E2DAFF] text-[#654EDA] border-[#C6B9FF] hover:bg-[#E2DAFF] focus-visible:ring-0',
            className
          )}
        >
          <TbWaveSine className="size-4" />
          {getDisplayText()}
          <LuChevronDown
            className={cn(
              'size-3.5 ml-0.5 transition-transform duration-200',
              open && 'rotate-180'
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[220px] p-0 border-[#EDEEF3] shadow-lg"
        align="start"
        sideOffset={6}
      >
        {/* Header */}
        <div className="px-3 py-2 border-b border-[#EDEEF3] flex items-center justify-between">
          <span className="text-[11px] font-medium text-[#8A8F98] uppercase tracking-wide">
            Modality
          </span>
          {selectedCount > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="text-[12px] text-[#8A8F98] hover:text-[#654EDA] transition-colors whitespace-nowrap cursor-pointer"
            >
              Clear
            </button>
          )}
        </div>

        {/* Content */}
        <div className="py-1">
          {/* Select All Option */}
          <div
            className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#F7F8F8] transition-colors border-b border-[#EDEEF3]"
            onClick={toggleSelectAll}
          >
            <Checkbox
              checked={isAllSelected ? true : isSomeSelected ? "indeterminate" : false}
              onCheckedChange={() => toggleSelectAll()}
              className="size-4 border-[#D1D5DB] data-[state=checked]:bg-[#654EDA] data-[state=checked]:border-[#654EDA] data-[state=indeterminate]:bg-[#654EDA] data-[state=indeterminate]:border-[#654EDA] cursor-pointer"
            />
            <span className="text-[13px] font-medium text-[#08090A]">
              Select All
            </span>
            <span className="ml-auto text-[12px] text-[#8A8F98]">
              {selectedCount}/{allModalities.length}
            </span>
          </div>

          {/* Modality Options */}
          {modalityOptions.map((option) => {
            const isSelected = isModalitySelected(option.value)
            return (
              <div
                key={option.value}
                className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#F7F8F8] transition-colors"
                onClick={(e) => toggleModality(option.value, e)}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleModality(option.value)}
                  className="size-4 border-[#D1D5DB] data-[state=checked]:bg-[#654EDA] data-[state=checked]:border-[#654EDA] cursor-pointer"
                />
                <span className={cn(
                  'text-[13px] flex-1',
                  isSelected ? 'text-[#08090A] font-medium' : 'text-[#08090A]'
                )}>
                  {option.label}
                </span>
                {isSelected && (
                  <LuCheck className="size-4 text-[#654EDA] shrink-0" />
                )}
              </div>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { ModalitySelector }
export type { ModalitySelection }
